import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Modal, ModalHeader, ModalBody, Card, CardTitle, CardSubtitle, CardText, Button } from 'reactstrap';
import { getModuleTypes, createModule, clearNewModule, getMyModuleTypes } from '../../../store/ModuleAction';
import StatusCode from '../../../util/StatusCode';
import Loading from '../../misc/Loading'
import CreateModuleModal from './CreateModuleModal';
import { CoreCreateModule, CoreFlowSyncModuleInfo, CoreModule, ModuleType } from '../../../models/CoreModels';
import { useTranslation } from 'react-i18next';
import { RequestState } from '../../../models/RequestState';
import { RootState } from '../../../store/store';
import { FlowSyncAccessPolicy, isAuthorized } from '../../../models/FlowSyncAccessPolicy';


interface ModulePresentationProps {
    isOpen: boolean
    toggleModal: () => void
}

const ModulePresentation = (props: ModulePresentationProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation('configuration');

    const [module, setModule] = useState<CoreFlowSyncModuleInfo | undefined>(undefined);
    const [title, setTitle] = useState<string>('');
    const [openCreateNew, setOpenCreateNew] = useState<boolean>(false);
    const [moduleType, setModuleType] = useState<ModuleType>(ModuleType.Undefined)

    const moduleTypes = useSelector<RootState, RequestState<CoreFlowSyncModuleInfo[]>>(state => state.module.moduleTypes);
    const newModule = useSelector<RootState, RequestState<CoreModule>>(state => state.module.createdModule);
    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const hasEditModulePermission = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_MODULE);

    useEffect(() => {
        getModuleTypes()(dispatch);
    }, [dispatch]);

    const getModuleTypeName = (id: number) => {
        switch (id) {
            case 1: return t('modules.import', { ns: 'common' });
            case 2: return t('modules.export', { ns: 'common' });
            default: return null;
        };
    };

    const getModuleTypeClass = (id: number) => {
        switch (id) {
            case 1: return 'Import';
            case 2: return 'Export';
            default: return null;
        };
    };

    const createNewModule = () => {
        if (module === undefined) {
            return;
        };

        let theNewModule: CoreCreateModule = {
            title: title,
            moduleType: module.moduleTypeName
        };
        createModule(theNewModule)(dispatch);
    };

    useEffect(() => {
        if (newModule.code === StatusCode.COMPLETE) {
            getMyModuleTypes(false)(dispatch);
            const moduleId = newModule.data.id;
            setTimeout(() => {
                closeModal();
                navigate(`/module/${moduleId}/configuration`);
            }, 1500)
        };
    }, [newModule])

    const updateTitle: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setTitle(e.target.value);
    };

    const renderCreateModule = (module: CoreFlowSyncModuleInfo) => {
        setOpenCreateNew(!openCreateNew);
        setModule(module);
    };

    const toggleModules = (moduleType: ModuleType) => {
        setModuleType(moduleType);
    };

    const filterModuleTypes = () => {
        const tabs = [
            { name: t('modulePresentation.showAll'), value: ModuleType.Undefined },
            { name: t('modulePresentation.import'), value: ModuleType.ImportModule },
            { name: t('modulePresentation.export'), value: ModuleType.ExportModule },
        ];
        return (
            <div className="module-toggle">
                {tabs.map(x =>
                    <Button key={x.value} className={moduleType === x.value ? 'active' : ''} onClick={() => toggleModules(x.value)}>{x.name}</Button>
                )}
            </div>
        );
    };

    const closeModal = () => {
        setOpenCreateNew(false);
        setTitle('');
        clearNewModule()(dispatch);
    };

    const renderModules = () => {
        if (moduleTypes.code === StatusCode.PENDING || moduleTypes.code === StatusCode.NONE) {
            return (<Loading />);
        };
        if (moduleTypes.code === StatusCode.ERROR) {
            return t('modulePresentation.create')
        };

        return (
            <>
                {filterModuleTypes()}
                {moduleTypes.data
                    .filter(m => moduleType === ModuleType.Undefined || m.moduleType === moduleType) 
                    .map(m =>
                        <Card key={m.id} className="module-presentation-card">
                            {m.vendorLogo ? <img className="logo vendor" src={m.vendorLogo} alt='' /> : <p className="vendorName vendor">{m.vendorName}</p>}
                            <CardTitle tag="h5">{t(`modules.${m.moduleTypeName}.title`, { ns: 'common' })}</CardTitle>
                            <span className="tags">
                                {m.moduleType !== 0 && <CardSubtitle className={getModuleTypeClass(m.moduleType) + " moduletype"}>{getModuleTypeName(m.moduleType)}</CardSubtitle>}
                                {!m.isReleased && <CardSubtitle className="not-released">{t('modulePresentation.unreleased')}</CardSubtitle>}
                            </span>
                            <CardText className="description">{t(`modules.${m.moduleTypeName}.description`, { ns: 'common' })}</CardText>
                            <Button onClick={() => renderCreateModule(m)} disabled={!m.hasLicense || !hasEditModulePermission} className={!hasEditModulePermission ? 'not-admin' : 'no-license'} color="primary">{t('modulePresentation.create')}</Button>
                        </Card>
                    )}
            </>
        );
    };

    return (
        <>
            <Modal size="xl" isOpen={props.isOpen} toggle={props.toggleModal}>
                <ModalHeader toggle={props.toggleModal}>{t('modulePresentation.title')}</ModalHeader>
                <ModalBody className="module-presentation-modal-body">
                    {renderModules()}
                </ModalBody>
            </Modal>

            {openCreateNew && module !== undefined && 
                <CreateModuleModal
                    isOpen={openCreateNew}
                    toggleModal={() => closeModal()}
                    module={module}
                    title={title}
                    updateTitle={(e) => updateTitle(e)}
                    createModule={createNewModule}
                    saveStatus={newModule.code}
                />}
        </>
    );
}

export default ModulePresentation;
